import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getWithTokenAPI, postWithTokenAPI } from "../util/Api";
import { Heart, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { useNavigate } from "react-router-dom";
import { setCurrentPage } from "../redux/navigationSlice.js";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "./ui/alert-dialog";
import { Sparkles, Funnel, ChevronDown, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { Popover, Box, Typography } from "@mui/material";
// Your icons
import TodayIcon from "@mui/icons-material/CalendarToday";
import WeekIcon from "@mui/icons-material/DateRange";
import { TrendingProfilesSlider } from "./Trending-profile-slider.jsx";
import { Toaster } from "react-hot-toast";
import { Footer } from "./Footer.jsx";
import { Helmet } from "react-helmet";
import ProfileCardsSkeleton from "./ProfileCardsSkeleton.jsx";
import { useTranslation } from "react-i18next";
import { setProfileCount } from "../redux/profileCountSlice.js";

export default function InterestSendList() {
  const data = useSelector((state) => state.user);
  const token = data?.userId?.accessToken;
  const id = data?.userId?.user?._id;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [interestList, setInterestList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [period, setPeriod] = useState("all");
  const completion = useSelector((state) => state.completion.value);
  const [openCompletionDialog, setOpenCompletionDialog] = useState(false);

      const [isMobile, setIsMobile] = useState(false);
  
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 500);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      
      return () => window.removeEventListener('resize', checkMobile);
    }, [])

  // OPEN FILTER
  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
    setOpen(true);
  };

  // CLOSE FILTER
  const handleFilterClose = () => {
    setOpen(false);
    setFilterAnchor(null);
  };

  // HANDLE DATE FILTER CHANGE
  const handlePeriodChange = (value) => {
    setPeriod(value);
    handleFilterClose();
    // You can filter viewerProfiles here if needed
    console.log("Selected Period:", value);
  };
  // Combined function to fetch interests and set sent IDs
  const fetchInterests = async () => {
    if (!token || !id) return;

    setLoading(true);
    try {
      const response = await getWithTokenAPI(
        token,
        `/interest/get_send_interest?user_id=${id}&period=${period}`
      );

      console.log("API response:", response);

      if (
        response?.statusCode === 200 &&
        Array.isArray(response?.data?.receivers)
      ) {
        const receivers = response.data.receivers;

        // ✅ FIX: Add interest: true to all profiles since they are all sent interests
        const receiversWithInterest = receivers.map((receiver) => ({
          ...receiver,
          interest: true, // All profiles in sent interests should have interest: true
        }));

        setInterestList(receiversWithInterest);
      } else if (response?.statusCode === 404) {
        setInterestList([]);
      } else {
        setError(response?.message || t("Failed to fetch sent interests"));
        setInterestList([]);
      }
    } catch (error) {
      console.error("Error fetching interests:", error);
      setError(t("Network error") + ": " + error.message);
      setInterestList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && token) {
      fetchInterests();
    }
  }, [id, token, period]);

  const handleProfileClick = async (profile) => {
    const progress = Math.round((completion / 4) * 100);
    if (!token) return;
    try {
      const profileId = profile?.basicProfile?.user_id || profile?.user_id;
      const profilename = profile?.basicProfile?.name || profile?.name;
      if (!profileId) {
        console.warn("No profile id found for", profile);
        return;
      }
      if (progress > 75) {
        const payload = { viewer_id: id, viewed_id: profileId };
        const response = await postWithTokenAPI(
          token,
          `/profile-view/track`,
          payload
        );
        if (response?.statusCode === 200) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          navigate(`/view-profile/${profilename.replace(/\s+/g, "-")}`, {
            state: { profileId: profileId },
          });
          dispatch(setProfileCount(response?.data?.unique_profiles_viewed_today));
        } else if (response?.statusCode === 403) {
          setShowSubscribeDialog(true);
        } else {
          console.log(response);
        }
      } else {
        setOpenCompletionDialog(true);
      }
    } catch (error) {
      console.error("Error in viewed profile post", error);
    }
  };

  // ---------------- ERROR STATE ----------------
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[70vh] bg-white rounded-lg border border-gray-200">
        <div className="text-center mt-6">
          <div className="text-red-500 text-lg mb-4">
            {t("Error:")} {error}
          </div>
          <Button
            onClick={fetchInterests}
            className="bg-red-500 hover:bg-red-600 text-white mb-6"
          >
            {t("Try Again")}
          </Button>
        </div>
      </div>
    );
  }

  // ---------------- EMPTY STATE ----------------
  if (interestList.length === 0 && !loading) {
    return (
      <>
        <Toaster position="top-right" />
        <Helmet>
          <title>Avan Aval - Sent Interests</title>
          <meta name="description" content="Sent interests dashboard" />
        </Helmet>
        <div className="flex justify-center items-center min-h-[70vh] bg-white rounded-lg border border-gray-200">
          <div className="text-center px-4 mt-6">
            <div className="mb-6">
              {/* <div className="w-20 h-20 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
                <Heart className="h-10 w-10 text-red-500" />
              </div> */}
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t("No Interests Sent Yet")}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                {t(
                  "You haven't sent any interests yet. Browse profiles and connect with people you like."
                )}
              </p>
            </div>
            <Button
              onClick={() => navigate("/browse")}
              className="bg-gradient-to-br from-red-500 to-pink-600 text-white cursor-pointer hover:from-red-600 hover:to-pink-700 mb-6"
            >
              {t("Browse Profiles")}
            </Button>
          </div>
        </div>
      </>
    );
  }

  // ---------------- MAIN RENDER ----------------
  return (
    <div className="min-h-[70vh] bg-white rounded-lg border border-gray-200">
      <Toaster position="top-right" />
      <Helmet>
        <title>Avan Aval - Sent Interests</title>
        <meta name="description" content="Sent interests dashboard" />
      </Helmet>

      {loading ? (
        <div className="min-h-[70vh] flex items-center justify-center">
          <ProfileCardsSkeleton card={10} />
        </div>
      ) : (
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center border-b p-4 m-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {t("Sent Interests")}
              </h3>
              <p className="text-gray-600 mt-1">
                {t("you have shown interest to")} {interestList.length}{" "}
                {interestList.length === 1 ? t("person") : t("people")}
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleFilterClick}
              className="flex items-center gap-2 px-6 py-3 bg-rose-100 border border-rose-500 rounded-xl text-rose-600 font-semibold hover:shadow-lg transition-all cursor-pointer mt-4 md:mt-0"
            >
              <Funnel size={18} />
              {!isMobile && <span className="inline sm:hidden" >{t("Filter")}</span>}
              <ChevronDown size={16} />
            </motion.button>
          </div>

          <Popover
            open={open}
            anchorEl={filterAnchor}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiPopover-paper": {
                borderRadius: "16px",
                boxShadow: "0 20px 40px rgba(225, 29, 72, 0.15)",
                border: "1px solid #fda4af",
                marginTop: "8px",
                minWidth: "220px",
                overflow: "hidden",
              },
            }}
          >
            <Box sx={{ p: 3, backgroundColor: "#fff1f2" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#e11d48",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 3,
                  fontSize: "14px",
                }}
              >
                <Calendar size={16} />
                {t("Select Period")}
              </Typography>

              {/* Options */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  { value: "today", label: t("Today"), icon: <TodayIcon /> },
                  { value: "week", label: t("This Week"), icon: <WeekIcon /> },
                  {
                                            value: "month",
                                            label: t("This Month"),
                                            icon: <WeekIcon />,
                                          },
                ].map((option) => (
                  <motion.button
                    key={option.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handlePeriodChange(option.value)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border font-semibold
            transition-all cursor-pointer
            ${
              period === option.value
                ? "text-white shadow-lg"
                : "bg-white text-gray-700 border-gray-200 hover:bg-rose-50 hover:text-rose-600"
            }`}
                    style={{
                      background: period === option.value ? "#be123c" : "",
                      borderColor: period === option.value ? "#9f1239" : "",
                    }}
                  >
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </motion.button>
                ))}
              </Box>

              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleFilterClose}
                className="w-full mt-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-600 font-medium hover:bg-gray-50 cursor-pointer"
              >
                {t("Close")}
              </motion.button>
            </Box>
          </Popover>

          {/* Profiles Slider Section */}
          <div className="min-h-[400px]">
            <TrendingProfilesSlider
              profiles={interestList}
              layoutType="Showall"
              onNavigate={(profile) => {
                const profileId =
                  profile?.basicProfile?.user_id ||
                  profile?.user_id ||
                  profile._id;
                console.log("Clicked profile id:", profileId);
                handleProfileClick(profile);
              }}
            />
          </div>
        </div>
      )}

      {/* Subscription Dialog */}
      <AlertDialog
        open={showSubscribeDialog}
        onOpenChange={setShowSubscribeDialog}
      >
        <AlertDialogContent className="max-w-sm text-center">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 text-xl font-semibold">
              🔔 {t("Subscription Required")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 mt-2">
              {t("Upgrade your plan to explore more matches")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex justify-center gap-3 mt-4">
            <AlertDialogCancel
              onClick={() => setShowSubscribeDialog(false)}
              className="cursor-pointer border border-gray-300 hover:bg-gray-100 px-4 py-2 rounded-lg"
            >
              {t("Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 cursor-pointer"
              onClick={() => {
                setShowSubscribeDialog(false);
                dispatch(setCurrentPage("subscription"));
              }}
            >
              {t("Subscribe Now")}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={openCompletionDialog}
        onOpenChange={setOpenCompletionDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Your Journey Starts Here")}</AlertDialogTitle>

            <AlertDialogDescription>
              {t(
                "Complete your profile to unlock full access and explore profiles"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setOpenCompletionDialog(false)}
              className="cursor-pointer border border-gray-300 hover:bg-gray-100 px-4 py-2 rounded-lg"
            >
              {t("Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 cursor-pointer"
              onClick={() => {
                setOpenCompletionDialog(false);
                navigate("/profile");
                dispatch(setCurrentPage("profile"));
              }}
            >
              {t("Continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
